// CreateSurvey.jsx
import {
  use, useState, useEffect, useRef, useCallback, useMemo, Suspense,
} from 'react';

import { useAuth } from '../../../context/AuthContext';
import {
  SharedSurveyForm, createInitialSurvey, createSurvey,
  SurveyLoader, SurveyErrorBoundary,
} from './Survey';
import { db } from '../../../config/firebase';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import {
  Alert, Button, Container, Typography, Snackbar, Slide, Stack, Chip, LinearProgress,
} from '@mui/material';
import { nanoid } from 'nanoid';

import { translations } from './surveyTranslations.js';

import {
  saveSurveyDraft, loadSurveyDraft, deleteSurveyDraft,
  clearExpiredDrafts, setCurrentDraftId,
} from './surveyLocalStorage.js';



import { checkSurveyExists } from './surveyUtils';

import { createPromiseCache } from './cacheUtils';

import SubscriptionStatusAlert from './ui/SubscriptionStatusAlert';



const NANO_ID_LENGTH = 16;

const existsCache = createPromiseCache();
function getSurveyExistsPromise(urlId) {
  return existsCache.getOrCreate(urlId, () => checkSurveyExists(urlId));
}

// ─── CreateSurveyInner ────────────────────────────────────────────────────
function CreateSurveyInner({
  uid, subscriptionActive, surveyUrlId, initialSurveyData,
  existingSurveyId: initialExistingId, hasDraftRestored,
  maxSurveys, maxQuestions, lang, t, onNavigate, currentUser,
  countPromise,
}) {
  const serverCount = use(countPromise);
  const [savedCount, setSavedCount]                   = useState(serverCount);
  const [existingSurveyId, setExistingSurveyId]       = useState(initialExistingId);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(!!initialExistingId);
  const [urlId, setUrlId]     = useState(surveyUrlId);
  const [forceReset, setForceReset] = useState(0);
  const [surveyData, setSurveyData] = useState(initialSurveyData);
  const [draftSnackOpen, setDraftSnackOpen] = useState(hasDraftRestored);

  const canCreate = savedCount < maxSurveys;
  const usagePct  = Math.min((savedCount / maxSurveys) * 100, 100);
  const isNearLimit = savedCount >= maxSurveys;

  const navigate = useCallback(
    (path) => {
      if (onNavigate) onNavigate(path);
      else window.history.replaceState(null, '', `/${path}`);
    },
    [onNavigate],
  );

  const handleCreateNewSurvey = useCallback(() => {
    const newId = nanoid(NANO_ID_LENGTH);
    setUrlId(newId);
    setCurrentDraftId(newId);
    setForceReset((n) => n + 1);
    setSurveyData(null);
    setExistingSurveyId(null);
    setShowDuplicateWarning(false);
    navigate(`platform/createSurvey/${newId}`);
  }, [navigate]);

  const handleSubmit = useCallback(
    async (formData) => {
      const check = await checkSurveyExists(formData.urlId);
      if (check.exists) {
        setExistingSurveyId(check.surveyId);
        setShowDuplicateWarning(true);
        throw new Error(
          'Esta encuesta ya fue guardada. Use el botón "Crear Nueva Encuesta" para crear otra.',
        );
      }
      const result = await createSurvey(formData);
      if (formData.urlId) deleteSurveyDraft(formData.urlId);
      setExistingSurveyId(result.id);
      setSavedCount((prev) => prev + 1);
      return result;
    },
    [],
  );

  const handleUpgradeClick  = useCallback(() => navigate('platform/suscripciones'), [navigate]);
  const handleDataChange    = useCallback(
    (data) => { if (urlId && data && !existingSurveyId) saveSurveyDraft(urlId, data); },
    [urlId, existingSurveyId],
  );
  const handleViewExistingSurvey = useCallback(() => {
    if (existingSurveyId) navigate(`platform/editSurvey/${existingSurveyId}`);
  }, [existingSurveyId, navigate]);

  const initialSurvey = useMemo(() => {
    if (!urlId) return null;
    if (surveyData) return { ...surveyData, urlId, createdBy: uid };
    return createInitialSurvey({ urlId }, uid, subscriptionActive);
  }, [urlId, uid, surveyData, subscriptionActive]);

  if (!canCreate) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          {t.limitReached.replace('{max}', maxSurveys)}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t.currentTotal.replace('{count}', savedCount)}
        </Typography>
        <Button variant="contained" color="primary" onClick={handleUpgradeClick}>
          {t.upgradeButton}
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Snackbar
        open={draftSnackOpen} autoHideDuration={4000}
        onClose={() => setDraftSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Slide} TransitionProps={{ direction: 'down' }}
      >
        <Alert onClose={() => setDraftSnackOpen(false)} severity="info" variant="filled" sx={{ width: '100%' }}>
          {lang === 'es'
            ? '📝 Borrador restaurado. Tus cambios anteriores han sido recuperados.'
            : '📝 Draft restored. Your previous changes have been recovered.'}
        </Alert>
      </Snackbar>

      {showDuplicateWarning && (
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Alert
            severity="warning"
            action={
              <>
                <Button color="inherit" size="small" onClick={handleViewExistingSurvey}>
                  {lang === 'es' ? 'Ver Encuesta' : 'View Survey'}
                </Button>
                <Button color="inherit" size="small" onClick={handleCreateNewSurvey}>
                  {lang === 'es' ? 'Crear Nueva' : 'Create New'}
                </Button>
              </>
            }
          >
            {lang === 'es'
              ? 'Esta encuesta ya fue guardada. Puedes verla/editarla o crear una nueva encuesta.'
              : 'This survey has already been saved. You can view/edit it or create a new survey.'}
          </Alert>
        </Container>
      )}

      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Stack spacing={1} alignItems="center">
          <Chip
            size="small"
            variant="outlined"
            color={isNearLimit ? 'error' : 'primary'}
            label={t.currentTotal.replace('{count}', savedCount)}
          />
          <LinearProgress
            variant="determinate"
            value={usagePct}
            color={isNearLimit ? 'error' : 'primary'}
            sx={{ width: '100%', maxWidth: 280, height: 6, borderRadius: 3 }}
          />
        </Stack>
      </Container>

      <SharedSurveyForm
        key={`${urlId}-${forceReset}`}
        initialSurvey={initialSurvey}
        isEditing={false}
        currentUser={currentUser}
        onSubmit={handleSubmit}
        onCreateNew={handleCreateNewSurvey}
        onDataChange={handleDataChange}
        currentSurveyCount={savedCount}
        maxSurveys={maxSurveys}
      />
    </>
  );
}

// ─── CreateSurveyInit ─────────────────────────────────────────────────────
function CreateSurveyInit({
  uid, subscriptionActive, onNavigate, currentUser,
  maxSurveys, maxQuestions, lang, t, countPromise,
}) {
  const currentPath = window.location.pathname;
  const match = currentPath.match(/\/platform\/createSurvey\/([a-zA-Z0-9_-]{16})$/);
  const [stableUrlId] = useState(() => match?.[1] ?? nanoid(NANO_ID_LENGTH));

  if (!match) {
    const newPath = `/platform/createSurvey/${stableUrlId}`;
    if (onNavigate) onNavigate(`platform/createSurvey/${stableUrlId}`);
    else window.history.replaceState(null, '', newPath);
    setCurrentDraftId(stableUrlId);
  }

  const existsCheck      = use(getSurveyExistsPromise(stableUrlId));
  const draft            = existsCheck.exists ? null : loadSurveyDraft(stableUrlId);
  const hasDraftRestored = !existsCheck.exists && draft != null;

  return (
    <CreateSurveyInner
      uid={uid} subscriptionActive={subscriptionActive}
      surveyUrlId={stableUrlId}
      initialSurveyData={existsCheck.exists ? existsCheck.data : draft}
      existingSurveyId={existsCheck.exists ? existsCheck.surveyId : null}
      hasDraftRestored={hasDraftRestored}
      maxSurveys={maxSurveys} maxQuestions={maxQuestions}
      lang={lang} t={t} onNavigate={onNavigate} currentUser={currentUser}
      countPromise={countPromise}
    />
  );
}

// ─── Componente público ───────────────────────────────────────────────────
const CreateSurvey = ({ onNavigate }) => {
  const {
    currentUser,
    isActive: subscriptionActive,
    planLimits,
    language,
    subscription,
  } = useAuth();

  const lang = language === 'es' ? 'es' : 'en';
  const t    = useMemo(() => translations[lang], [lang]);

  const maxSurveys   = planLimits?.maxSurveys   ?? 5;
  const maxQuestions = planLimits?.maxQuestions ?? 5;

  useEffect(() => { clearExpiredDrafts(); }, []);

  const countPromiseRef = useRef(null);
  if (currentUser?.uid && countPromiseRef.current === null) {
    countPromiseRef.current = getCountFromServer(
      query(
        collection(db, 'surveys'),
        where('createdBy', '==', currentUser.uid),
        where('isDelete', '==', false),
      ),
    ).then((snap) => snap.data().count ?? 0);
  }

  if (!currentUser?.uid) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Alert severity="warning">
          {lang === 'es' ? 'Por favor inicia sesión.' : 'Please log in.'}
        </Alert>
      </Container>
    );
  }

  return (
    <SurveyErrorBoundary
      lang={lang}
      errorMessage={
        lang === 'es'
          ? 'Error al inicializar el formulario.'
          : 'Error initializing the form.'
      }
    >
      <SubscriptionStatusAlert
        subscriptionActive={subscriptionActive}
        subscription={subscription}
        lang={lang}
      />

      <Suspense fallback={<SurveyLoader t={t} />}>
        <CreateSurveyInit
          uid={currentUser.uid}
          subscriptionActive={subscriptionActive}
          onNavigate={onNavigate}
          currentUser={currentUser}
          maxSurveys={maxSurveys}
          maxQuestions={maxQuestions}
          lang={lang}
          t={t}
          countPromise={countPromiseRef.current}
        />
      </Suspense>
    </SurveyErrorBoundary>
  );
};

export default CreateSurvey;